/**
 * Canonical URL SHAPE — ONE definition shared by the Next app and the edge
 * workers (rules 23/24).
 *
 * ishvaram.com serves every HTML route with a trailing slash: `/numerology`
 * 301s to `/numerology/`, `/numerology/5` 307s to `/numerology/5/`, and every
 * `<link rel=canonical>` is slashed (verified live 2026-08-09). A schema.org
 * breadcrumb `item` that omits the slash therefore points at a redirect.
 *
 * `withTrailingSlash` used to be a PRIVATE helper inside
 * `edge-db/shared/internal-href-canonical.ts`, unreachable from `src/`, which is
 * why 53 distinct hand-typed breadcrumb literals drifted. Edge/exporter
 * consumers import this with an explicit `.ts` extension because the production
 * exporter runs under bare Node (rule 64); `src/` imports it extensionless.
 */

export const SITE_ORIGIN = "https://ishvaram.com";

/** A path is canonical WITH a trailing slash — except root and file-like leaves. */
export function withTrailingSlash(path: string): string {
  if (path === "/") return path;
  if ((path.split("/").pop() ?? "").includes(".")) return path;
  return path.endsWith("/") ? path : `${path}/`;
}

/**
 * Canonical absolute URL for a breadcrumb/nav `item`. Internal values (a bare
 * path, or an absolute ishvaram.com URL) get the canonical slash; any query or
 * fragment is split off first so `/x?y` never becomes `/x?y/`. A FOREIGN
 * absolute URL is returned untouched — this owns OUR canonical shape, not
 * somebody else's (pinned by `src/lib/schema.test.ts`'s `https://other.test/x`).
 */
export function canonicalItemUrl(value: string): string {
  if (/^https?:\/\//i.test(value) && !value.startsWith(SITE_ORIGIN)) return value;
  const relative = value.startsWith(SITE_ORIGIN) ? value.slice(SITE_ORIGIN.length) : value;
  if (relative === "") return `${SITE_ORIGIN}/`;
  if (!relative.startsWith("/")) return value;
  const suffixStart = relative.search(/[?#]/);
  const path = suffixStart === -1 ? relative : relative.slice(0, suffixStart);
  const suffix = suffixStart === -1 ? "" : relative.slice(suffixStart);
  return `${SITE_ORIGIN}${withTrailingSlash(path)}${suffix}`;
}
